/**
 * API client for the web app.
 *
 * Sprint 7 — OIDC/BFF. The browser no longer carries tenant / role
 * headers; tenant + roles come from the signed session cookie the
 * BFF mints after a successful OIDC login.
 *
 * Reads:
 *  - `VITE_API_BASE_URL` (e.g. `/api` in staging, `http://localhost:4000/v1` in dev)
 *
 * All requests:
 *   - send cookies via `credentials: "include"` (required for the
 *     signed session cookie to round-trip across origins in dev;
 *     same-origin in staging).
 *   - throw `ApiError` on non-2xx. A 401 response means the session
 *     has expired or is missing — callers must redirect to
 *     `/v1/auth/login?return_to=<current>`.
 */

import { isHeavyReadUrl, noteHeavyReadRateLimited } from "./apiBackoff.js";
import { notifyModuleRefusal } from "./moduleRefusalNotice.js";
import { clearClientStateForLogout } from "./tenantScopedStorage.js";

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "/api";

export const API_CONTEXT = {
  baseUrl: API_BASE,
} as const;

export class ApiError extends Error {
  /**
   * Sprint 16 Wave H — `x-request-id` from the failed response.
   * Lets the UI surface the id alongside the error message so an
   * operator can correlate a user-visible failure to the
   * structured access-log row on the API side. `undefined` when the
   * response did not include the header (e.g. CORS preflight, or a
   * dev API without the access-log middleware).
   */
  readonly requestId: string | undefined;

  constructor(
    readonly status: number,
    readonly url: string,
    message: string,
    readonly body?: unknown,
    requestId?: string,
  ) {
    super(`[${status}] ${url}: ${message}`);
    this.name = "ApiError";
    this.requestId = requestId;
  }
}

export class UnauthenticatedError extends ApiError {
  constructor(url: string, body?: unknown, requestId?: string) {
    super(401, url, "unauthenticated", body, requestId);
    this.name = "UnauthenticatedError";
  }
}

/**
 * GT-UI-API-RATE-LIMIT-REQUEST-STORM-FIX-02 — typed HTTP 429.
 *
 * The global API limiter (and the per-route budgets added in this
 * sprint) answer over-limit requests with `429 { error: "rate_limited",
 * message }` and a `Retry-After` header. Surfacing 429 as its own error
 * type lets the QueryClient retry policy (see `rateLimitRetry.ts`) wait
 * for the server-advertised window instead of hammering it immediately
 * — which is what turned a brief burst into a sustained storm before.
 */
export class RateLimitError extends ApiError {
  /**
   * Seconds the server asked us to wait, parsed from `Retry-After`.
   * `null` when the header was absent or unparseable (callers then fall
   * back to exponential backoff).
   */
  readonly retryAfterSeconds: number | null;
  constructor(url: string, retryAfterSeconds: number | null, body?: unknown, requestId?: string) {
    super(429, url, "rate_limited", body, requestId);
    this.name = "RateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * F-MANDANTENSTATUS1 — the API refused a read because the tenant is
 * suspended or its read window has ended (`403 tenant_subscription_blocked`).
 * Only GET responses dispatch this event: a paused or read-only tenant
 * refuses writes with the same 403, and that must not blank the page.
 * The shell listens and shows the full-page block.
 */
export const TENANT_READS_BLOCKED_EVENT = "gt:tenant-reads-blocked";

/**
 * Parse an HTTP `Retry-After` header into whole seconds. Supports both
 * forms from RFC 7231 §7.1.3: delta-seconds (`"30"`) and an HTTP-date
 * (`"Wed, 21 Oct 2026 07:28:00 GMT"`). Returns `null` when absent /
 * unparseable, `0` for a past date, and clamps to a 1-hour ceiling so a
 * hostile header cannot pin a query in a multi-hour backoff. `now` is
 * injectable for deterministic tests.
 */
export function parseRetryAfterSeconds(
  headerValue: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (headerValue == null) return null;
  const trimmed = headerValue.trim();
  if (trimmed === "") return null;
  const MAX = 3600;
  if (/^\d+$/.test(trimmed)) {
    const secs = Number(trimmed);
    return Number.isFinite(secs) ? Math.min(secs, MAX) : null;
  }
  const when = Date.parse(trimmed);
  if (Number.isFinite(when)) {
    const secs = Math.ceil((when - now) / 1000);
    return Math.min(Math.max(secs, 0), MAX);
  }
  return null;
}

/**
 * Sprint 26 Wave C — exported so non-fetch consumers (e.g. anchor
 * `href` attributes for direct CSV downloads) can build the same
 * base-aware URL as the rest of the app, instead of concatenating
 * `apiBase + "/v1/..."` themselves.
 */
export function buildApiUrl(
  path: string,
  params?: Record<string, string | number | undefined>,
): string {
  return buildUrl(path, params);
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
  const base = API_BASE.replace(/\/+$/, "");
  const url = new URL(`${base}${path.startsWith("/") ? path : `/${path}`}`, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

async function parseErrorBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return undefined;
  }
}

/**
 * 003562 (#3255) — ONE raw-response entry point for the export runners.
 *
 * `send()` and `apiDownload()` report a module-enforcement refusal
 * (`403 module_not_booked` / `module_read_only`) centrally via
 * `notifyModuleRefusal`, so the user is told the module is not booked even
 * when the page renders its own generic error state. The export runners
 * cannot use either helper: they need the raw `Response` (streamed blob,
 * `content-disposition` / `x-report-row-count` headers, their own
 * per-route error codes). Before this helper existed each of them called
 * `fetch()` itself and parsed 403 on its own — bypassing the central
 * notice (Lane B review 003558, NO-GO).
 *
 * `apiFetchRaw` is that shared helper: it resolves the API base the same
 * way as every other request, peeks a failed response for a module
 * refusal, and hands the UNREAD response back to the caller. The peek
 * reads a `clone()`, so the caller's own `res.json()` / `res.text()` /
 * `res.blob()` still sees a full body.
 */
export async function apiFetchRaw(
  path: string,
  init: RequestInit,
  params?: Record<string, string | number | undefined>,
): Promise<Response> {
  const res = await fetch(buildUrl(path, params), init);
  if (!res.ok) await notifyModuleRefusalFromResponse(res);
  return res;
}

async function notifyModuleRefusalFromResponse(res: Response): Promise<void> {
  // A refusal body may only be read once — peek a clone so the caller
  // keeps the untouched body. A response without `clone()` is not a real
  // `Response` (hand-written test double); skip rather than consume the
  // body the caller still needs.
  if (typeof res.clone !== "function") return;
  const requestId = res.headers.get("x-request-id") ?? undefined;
  notifyModuleRefusal(res.status, await parseErrorBody(res.clone()), requestId);
}

async function send<T>(url: string, init: RequestInit): Promise<T> {
  // GT-RATE-LIMIT-ROOT-CAUSE-DRIVEN-FIX-07 — NO client-side fail-fast.
  //
  // FIX-05 short-circuited every heavy GET while a global backoff window
  // was open. The forensic analysis (FIX-06) proved that was the
  // proximate cause of the owner-visible failures: a single 429 from one
  // route (e.g. a Live-Map poll) made UNRELATED user navigation —
  // opening Vehicles / Trips — fail-fast with a fabricated 429 before it
  // even called the server, for up to 60 s. The window is now relieved by
  // pausing BACKGROUND polling (heavy poll hooks defer via
  // `heavyPollIntervalMs`), never by blocking a user-initiated request.
  const res = await fetch(url, init);
  if (res.status === 204) {
    return undefined as T;
  }
  if (!res.ok) {
    const body = await parseErrorBody(res);
    // Sprint 16 Wave H — capture `x-request-id` from the response so
    // the UI can include it in error toasts. Lower-cased per HTTP/1.1
    // header-name normalization.
    const requestId = res.headers.get("x-request-id") ?? undefined;
    if (res.status === 401) {
      throw new UnauthenticatedError(url, body, requestId);
    }
    if (res.status === 429) {
      const retryAfter = parseRetryAfterSeconds(res.headers.get("retry-after"));
      // RECORD the window so heavy POLLING hooks defer their next refetch
      // (relieving the shared bucket) — but do NOT block any request here.
      const isGet = (init.method ?? "GET").toUpperCase() === "GET";
      if (isGet && isHeavyReadUrl(url)) noteHeavyReadRateLimited(retryAfter);
      throw new RateLimitError(url, retryAfter, body, requestId);
    }
    if (
      res.status === 403 &&
      (body as { error?: unknown } | null)?.error === "tenant_subscription_blocked" &&
      (init.method ?? "GET").toUpperCase() === "GET"
    ) {
      window.dispatchEvent(new Event(TENANT_READS_BLOCKED_EVENT));
    }
    notifyModuleRefusal(res.status, body, requestId);
    throw new ApiError(res.status, url, res.statusText, body, requestId);
  }
  return (await res.json()) as T;
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string | number | undefined>,
  signal?: AbortSignal,
): Promise<T> {
  const url = buildUrl(path, params);
  const init: RequestInit = {
    method: "GET",
    headers: { accept: "application/json" },
    credentials: "include",
  };
  if (signal) init.signal = signal;
  return send<T>(url, init);
}

/**
 * Sprint 42A (GT-STAGING-FINDINGS-RECONCILE-42A) — authenticated
 * file download.
 *
 * Required because Owner-reported finding F37-018: clicking a CSV
 * import-template `<a href={apiUrl} download>` link opened a new
 * navigation context that lost the session cookie and the API
 * returned `401 unauthenticated`.
 *
 * Fix: fetch the file via `credentials: "include"`, hold the body
 * in a `Blob`, and trigger a download via a temporary
 * `URL.createObjectURL` link inside the authenticated SPA tab.
 *
 * Throws `UnauthenticatedError` on `401` (caller redirects to
 * login) and `ApiError` on every other non-2xx so the existing
 * error UX continues to work.
 */
export async function apiDownload(
  path: string,
  params?: Record<string, string | number | undefined>,
  signal?: AbortSignal,
): Promise<Blob> {
  const url = buildUrl(path, params);
  const init: RequestInit = {
    method: "GET",
    // No `accept: application/json` — let the server pick the
    // content-type. We accept anything; the response body is held as
    // a Blob.
    credentials: "include",
  };
  if (signal) init.signal = signal;
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = await parseErrorBody(res);
    const requestId = res.headers.get("x-request-id") ?? undefined;
    if (res.status === 401) {
      throw new UnauthenticatedError(url, body, requestId);
    }
    if (res.status === 429) {
      throw new RateLimitError(
        url,
        parseRetryAfterSeconds(res.headers.get("retry-after")),
        body,
        requestId,
      );
    }
    notifyModuleRefusal(res.status, body, requestId);
    throw new ApiError(res.status, url, res.statusText, body, requestId);
  }
  return await res.blob();
}

/**
 * Sprint 42A — convenience wrapper that fetches a file via
 * `apiDownload` and triggers a save in the user's browser. Used by
 * the Imports CSV template card and any future authenticated
 * file-download surface.
 *
 * The implementation creates a transient `<a>` element with an
 * object URL, programmatically clicks it, and revokes the URL
 * immediately afterwards. This keeps the download inside the
 * authenticated SPA context so cookies travel with the request.
 *
 * Tests: see `services/api/src/sprint-42a.test.ts` (source guards
 * pin no `<a href={apiUrl}>` patterns for protected routes) and
 * `apps/web/src/sprint-42a.test.tsx`.
 */
export async function apiDownloadAndSave(
  path: string,
  filename: string,
  params?: Record<string, string | number | undefined>,
  signal?: AbortSignal,
): Promise<void> {
  const blob = await apiDownload(path, params, signal);
  const objectUrl = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function apiSend<T>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const url = buildUrl(path);
  const init: RequestInit = {
    method,
    headers: {
      accept: "application/json",
      ...(body === undefined ? {} : { "content-type": "application/json" }),
    },
    credentials: "include",
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;
  return send<T>(url, init);
}

/**
 * Sprint 14 Wave D — multipart upload helper.
 *
 * Used by `/imports` to POST a `multipart/form-data` body to
 * endpoints like `POST /v1/imports/vehicles?action=dry-run`. Browser
 * sets the multipart boundary on the `Content-Type` header
 * automatically when the body is a `FormData`; we must therefore
 * **not** set `content-type` ourselves.
 */
export async function apiUpload<T>(
  path: string,
  formData: FormData,
  params?: Record<string, string | number | undefined>,
  signal?: AbortSignal,
): Promise<T> {
  const url = buildUrl(path, params);
  const init: RequestInit = {
    method: "POST",
    headers: { accept: "application/json" },
    credentials: "include",
    body: formData,
  };
  if (signal) init.signal = signal;
  return send<T>(url, init);
}

/**
 * SessionStorage flag set immediately before navigating to the
 * BFF /v1/auth/login. After the user comes back from Keycloak via
 * /v1/auth/callback, the SessionProvider reads this flag and applies
 * a single-retry grace period on its initial /v1/auth/session fetch
 * to absorb any cookie-propagation race (Sprint 47.1, Owner-walkthrough
 * Punkt 14: "/admin/ Login flaky (OIDC race)" — same shape applies to
 * the customer SPA so both consume the helper).
 */
export const POST_LOGIN_GRACE_FLAG = "gt.session.post-login-grace";

/**
 * Redirect the browser to the BFF login endpoint. The BFF will
 * round-trip through Keycloak and land the user back on `returnTo`
 * (defaults to the current URL).
 */
export function redirectToLogin(returnTo: string = window.location.href): void {
  try {
    window.sessionStorage.setItem(POST_LOGIN_GRACE_FLAG, "1");
  } catch {
    // SessionStorage may be unavailable (private mode, quota). Skip
    // the grace flag — initial fetch still works, just without retry.
  }
  const base = API_BASE.replace(/\/+$/, "");
  const url = new URL(`${base}/v1/auth/login`, window.location.origin);
  url.searchParams.set("return_to", returnTo);
  window.location.assign(url.toString());
}

/**
 * Sprint 23 Wave B (S23-B5) — subscription projection surfaced on
 * `GET /v1/auth/session`. Optional because (a) older tokens or
 * platform contexts that don't have a tenant row return no
 * projection and (b) the BFF intentionally omits the field if the
 * platform-side lookup fails so customer-side reads are never
 * blocked (operator decision Q2).
 */
export type SubscriptionStatus =
  | "trial"
  | "active"
  | "paused"
  | "suspended"
  | "cancelled"
  | "expired";
export type TenantType = "platform_only" | "managed_hardware" | "hybrid";

export interface SubscriptionInfo {
  status: SubscriptionStatus;
  type: TenantType;
  validUntil: string | null;
  trialUntil: string | null;
  /**
   * Sprint 29.4.1 owner finding #8 — tenant `display_name` from
   * the platform DB. Surfaced via the existing session projection
   * (no new endpoint). Null when the tenant has no display name
   * configured; the SPA falls back to the operator email in that
   * case.
   */
  displayName?: string | null;
}

export interface SessionInfo {
  identity: { sub: string; email: string; displayName: string | null };
  tenantId: string;
  roles: string[];
  expiresAt: string;
  /**
   * Sprint 23 Wave B subscription banner data.
   *
   * Sprint 29.4.1 owner finding #8 — also carries
   * `subscription.displayName`, the tenant's `display_name` row.
   * The customer SPA header reads this and renders it in place of
   * the operator email.
   */
  subscription?: SubscriptionInfo;
}

export async function fetchSession(signal?: AbortSignal): Promise<SessionInfo | null> {
  try {
    return await apiGet<SessionInfo>("/v1/auth/session", undefined, signal);
  } catch (err) {
    if (err instanceof UnauthenticatedError) return null;
    throw err;
  }
}

/**
 * Sprint 47.1 — Owner-walkthrough Punkt 14 ("/admin/ Login flaky (OIDC
 * race)"). When the user returns from Keycloak via the BFF callback,
 * the session cookie is set on the redirect response, but a sporadic
 * race occasionally lets the first /v1/auth/session fetch race ahead
 * of the cookie being written into the document jar. The result was
 * a redirect loop: 401 → /v1/auth/login → Keycloak SSO already
 * authenticated → bounce back to /admin/ → 401 again, etc.
 *
 * This wrapper applies a single 500ms-delayed retry only when the
 * caller indicates we are within the post-login grace window
 * (via the `POST_LOGIN_GRACE_FLAG` sessionStorage key, written by
 * `redirectToLogin`). The grace flag is consumed (cleared) on the
 * first call so a second 401 does not retry again — preventing any
 * silent infinite loop. If sessionStorage is unavailable, behaviour
 * falls back to the legacy single-fetch path.
 */
export async function fetchSessionWithPostLoginGrace(
  signal?: AbortSignal,
): Promise<SessionInfo | null> {
  const first = await fetchSession(signal);
  if (first) return first;

  let inGrace: boolean;
  try {
    inGrace = window.sessionStorage.getItem(POST_LOGIN_GRACE_FLAG) === "1";
    if (inGrace) window.sessionStorage.removeItem(POST_LOGIN_GRACE_FLAG);
  } catch {
    return null;
  }
  if (!inGrace) return null;

  await new Promise<void>((resolve) => setTimeout(resolve, 500));
  if (signal?.aborted) return null;
  return fetchSession(signal);
}

export async function logout(): Promise<string> {
  try {
    const res = await apiSend<{ endSessionUrl: string }>("POST", "/v1/auth/logout");
    return res.endSessionUrl;
  } finally {
    // GT-TENANCY-TRIPS-FILTER-VEHICLE-SCOPE-HOTFIX-01 — clear
    // tenant-sensitive client state on logout so a shared browser does
    // not carry one user's filter selection into the next session.
    // Pre-hotfix UNSCOPED keys are removed; the current identity's
    // scoped recall is retained (a same-user re-login still restores it
    // — Owner finding 7). The in-memory React Query cache is wiped by
    // the Keycloak end-session redirect reload that follows.
    clearClientStateForLogout();
  }
}
